import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { useNotification } from '../../context/NotificationContext';
import { 
  FaHome, FaTicketAlt, FaTrophy, FaHandshake, FaUser, 
  FaWallet, FaBell, FaBars, FaTimes, FaCog,
  FaSignOutAlt, FaChartLine, FaSearch, FaStar,
  FaCrown, FaPlus, FaShieldAlt
} from 'react-icons/fa';
import { formatXPUNT } from '../../utils/helpers';
import './Header.css';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { balance } = useWallet();
  const { unreadCount } = useNotification();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const isPunterPlus = user?.punter_plus || false;
  const isPunterPlusActive = user?.punter_plus && user?.punter_plus_status !== 'expired';
  const isAdmin = user?.is_admin || 
                user?.capabilities?.manage_options || 
                user?.roles?.includes('administrator') ||
                user?.roles?.includes('admin');

  // Don't redirect - just show the header

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="header-logo">
          <span className="logo-icon">
            <img src='/logo.png' style={{width: 30, height: 30}}/>
          </span>
          <span className="logo-text">XPunters</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="nav-link">
                <FaTicketAlt /> Dashboard
              </Link>
             
              {isAdmin && (
                <Link to="/admin" className="nav-link admin-link">
                  <FaShieldAlt /> Admin
                </Link>
              )}
            </>
          ) : (
            <> 
            </>
          )}
        </nav>


        {/* Search Bar */}
        
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search bet codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
           
        </form>

        {/* User Actions */}
        <div className="header-actions">
          {isAuthenticated ? (
            <>
              {/* Admin Button - Mobile/Compact */}
              {isAdmin && (
                <Link to="/admin" className="admin-btn-mobile" title="Admin Dashboard">
                  <FaShieldAlt />
                </Link>
              )}

              {/* Punter+ Button */}
              <Link to="/subscription" className="punter-plus-btn">
                {isPunterPlusActive ? (
                  <>
                    <FaCrown className="crown-icon" />
                    <span className="punter-label">Punter+</span>
                    {user?.punter_plus_expiry && (
                      <span className="expiry-badge">
                        {Math.ceil((new Date(user.punter_plus_expiry) - new Date()) / (1000 * 60 * 60 * 24))}d
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <FaPlus className="plus-icon" />
                    <span className="punter-label">Get Punter+</span>
                  </>
                )}
              </Link>

              <Link to="/wallet" className="wallet-link">
                <FaWallet />
                <span className="wallet-balance">{formatXPUNT(balance)}</span>
              </Link>
              
              <Link to="/notifications" className="notification-link">
                <FaBell />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </Link>

              <div className="user-menu">
                <button 
                  className="user-menu-trigger"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {user?.display_name?.[0] || 'U'}
                </button>
                <div className={`user-dropdown ${mobileMenuOpen ? 'open' : ''}`}>
                  {isAdmin && (
                    <>
                      <Link to="/admin" className="dropdown-item admin-item">
                        <FaShieldAlt /> Admin Dashboard
                      </Link>
                      <hr className="dropdown-divider" />
                    </>
                  )}
                  <Link to="/dashboard" className="dropdown-item">
                    <FaHome /> Dashboard
                  </Link>
                  <Link to="/profile" className="dropdown-item">
                    <FaUser /> Profile
                  </Link>
                  <Link to="/settings" className="dropdown-item">
                    <FaCog /> Settings
                  </Link>
                  <Link to="/wallet" className="dropdown-item">
                    <FaWallet /> Wallet
                  </Link>
                  <Link to="/subscription" className="dropdown-item subscription-item">
                    {isPunterPlusActive ? (
                      <>
                        <FaCrown className="crown-icon" /> Punter+ Active
                      </>
                    ) : (
                      <>
                        <FaStar /> Upgrade to Punter+
                      </>
                    )}
                  </Link>
                  {isPunterPlusActive && user?.creator_subscription_active && (
                    <Link to="/creator/dashboard" className="dropdown-item creator-item">
                      <FaChartLine /> Creator Dashboard
                    </Link>
                  )}
                  <hr className="dropdown-divider" />
                  <button onClick={handleLogout} className="dropdown-item logout">
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="auth-actions">
              <Link to="/login" className="auth-link login-link">Login</Link>
              <Link to="/register" className="auth-link register-link">Sign Up</Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav">
          <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <FaHome /> Home
          </Link>
          <Link to="/bet-codes" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <FaTicketAlt /> Bet Codes
          </Link>
          <Link to="/competitions" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <FaTrophy /> Competitions
          </Link>
          {isAuthenticated && (
            <Link to="/h2h" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <FaHandshake /> H2H
            </Link>
          )}
          <Link to="/leaderboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <FaChartLine /> Leaderboard
          </Link>
          {isAdmin && (
            <Link to="/admin" className="mobile-nav-link admin-link" onClick={() => setMobileMenuOpen(false)}>
              <FaShieldAlt /> Admin Dashboard
            </Link>
          )}
          {isAuthenticated && (
            <>
              <Link to="/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                <FaHome /> Dashboard
              </Link>
              <Link to="/subscription" className="mobile-nav-link punter-link" onClick={() => setMobileMenuOpen(false)}>
                {isPunterPlusActive ? (
                  <><FaCrown className="crown-icon" /> Punter+ Active</>
                ) : (
                  <><FaStar /> Upgrade to Punter+</>
                )}
              </Link>
              <Link to="/profile" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                <FaUser /> Profile
              </Link>
              <Link to="/wallet" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                <FaWallet /> Wallet ({formatXPUNT(balance)})
              </Link>
              {isPunterPlusActive && user?.creator_subscription_active && (
                <Link to="/creator/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  <FaChartLine /> Creator Dashboard
                </Link>
              )}
              <button onClick={handleLogout} className="mobile-nav-link logout-btn">
                <FaSignOutAlt /> Logout
              </button>
            </>
          )}
        </nav>
        {!isAuthenticated && (
          <div className="mobile-auth">
            <Link to="/login" className="mobile-auth-link login">Login</Link>
            <Link to="/register" className="mobile-auth-link register">Sign Up</Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;